import { Alert } from 'antd';
import { useState } from 'react';
import { ReadinessItemRow } from './ReadinessItemRow';
import { ReadinessOverrideModal } from './ReadinessOverrideModal';
import { ReadinessSummary } from './ReadinessSummary';
import type { JobDetail, ReadinessItem } from '../../../jobs.api';
import { useJobReadiness } from '../hooks/use-job-readiness';

interface JobReadinessTabProps {
  job: JobDetail;
  canManage: boolean;
  onChanged: () => Promise<void>;
}

/** The computed checklist of gates before dispatch (PRD → Job intake → Gates before dispatch; gitaumoses4/chukua#36) — shown, never blocking a job's own state transitions. */
export function JobReadinessTab({ job, canManage, onChanged }: JobReadinessTabProps) {
  const { attachError, overrideError, attach, override } = useJobReadiness(job.id, onChanged);
  const [overriding, setOverriding] = useState<ReadinessItem | null>(null);

  async function submitOverride(approverId: string, reason: string): Promise<boolean> {
    if (!overriding) return false;
    const saved = await override(overriding.key, approverId, reason, overriding.label);
    if (saved) setOverriding(null);
    return saved;
  }

  return (
    <div className="flex flex-col gap-4">
      <ReadinessSummary readiness={job.readiness} />
      {attachError && <Alert type="error" title={attachError} showIcon />}
      {overrideError && <Alert type="error" title={overrideError} showIcon />}
      <ul className="flex flex-col gap-3">
        {job.readiness.items.map((item) => (
          <ReadinessItemRow
            key={item.key}
            item={item}
            jobId={job.id}
            attachments={job.attachments}
            canManage={canManage}
            onOverride={setOverriding}
            onAttach={(requiredDocumentTypeId, uploadId) => attach(requiredDocumentTypeId, uploadId, item.label)}
          />
        ))}
      </ul>
      <ReadinessOverrideModal item={overriding} onCancel={() => setOverriding(null)} onSubmit={submitOverride} />
    </div>
  );
}
